import { matchPath } from "react-router";
import { type FolderItem } from "@/types/fse";
import { type UploadDestination } from "./uploadCoordinator";

const isNonEmptyString = (value: unknown): value is string => typeof value === "string" && value.trim().length > 0;

export const hasResourceBoundOwnerPermission = (
  folder: Pick<FolderItem, "id" | "permissions">,
  authenticatedUserId: string,
): boolean => {
  if (!isNonEmptyString(authenticatedUserId) || !isNonEmptyString(folder.id) || !Array.isArray(folder.permissions)) {
    return false;
  }

  const permissionsAreWellFormed = folder.permissions.every(
    permission =>
      typeof permission === "object" &&
      permission !== null &&
      isNonEmptyString(permission.id) &&
      isNonEmptyString(permission.fse_id) &&
      isNonEmptyString(permission.user_id) &&
      (permission.role === "owner" || permission.role === "viewer"),
  );
  if (!permissionsAreWellFormed) return false;

  const currentPrincipalPermissions = folder.permissions.filter(
    permission => permission.user_id === authenticatedUserId,
  );
  return (
    currentPrincipalPermissions.length === 1 &&
    currentPrincipalPermissions[0]?.role === "owner" &&
    currentPrincipalPermissions[0].fse_id === folder.id
  );
};

export const toUploadDestination = (
  folder: FolderItem,
  authenticatedUserId: string,
  isRoot = false,
): UploadDestination => {
  const hasValidIdentity =
    isNonEmptyString(authenticatedUserId) && isNonEmptyString(folder.id) && isNonEmptyString(folder.name);
  return {
    fseId: folder.id,
    name: folder.name,
    canUpload: hasValidIdentity && (isRoot || hasResourceBoundOwnerPermission(folder, authenticatedUserId)),
  };
};

export const resolveUploadDestination = (
  pathname: string,
  rootFolder: FolderItem | undefined,
  currentFolder: FolderItem | undefined,
  authenticatedUserId: string,
): UploadDestination | undefined => {
  if (authenticatedUserId.trim().length === 0) return undefined;
  const isRootDestination = matchPath("/drive/:fseId", pathname) === null;
  const folder = isRootDestination ? rootFolder : currentFolder;
  return folder ? toUploadDestination(folder, authenticatedUserId, isRootDestination) : undefined;
};

export const getCurrentDriveFolderId = (pathname: string): string | undefined =>
  matchPath("/drive/:fseId", pathname)?.params.fseId;
